import type { ReactElement } from "react"
import type { Components } from "react-markdown"
import ReactMarkdown from "react-markdown"
import rehypeRaw from "rehype-raw"

export interface BaseMarkdownProps {
  children: string
  components?: Components
}

// Normalize multiple spaces after list markers to a single space.
// `1.  Item` → `1. Item` — extra spaces shift the indentation threshold in CommonMark.
function normalizeListMarkerSpacing(content: string): string {
  return content.replace(/^([ \t]*(?:\d+[.)>]|[-*+]))[ \t]{2,}/gm, "$1 ")
}

// A CommonMark "loose" list (items separated by blank lines) wraps each item's
// content in <p> tags, which adds unwanted top/bottom margin. Convert loose
// lists to tight lists by removing blank lines between list items.
// Only collapses blank lines where BOTH sides are list items — avoids collapsing
// the blank line that separates a raw HTML block (e.g. <br>) from the first list item,
// which would cause CommonMark to treat the list as part of the HTML block.
function collapseLooseLists(content: string): string {
  return content.replace(
    /(^[ \t]*(?:\d+[.)>]|[-*+])[ \t][^\n]*)\n[ \t]*\n(?=[ \t]*(?:\d+[.)>]|[-*+])[ \t])/gm,
    "$1\n"
  )
}

// CommonMark does not process markdown inside raw HTML blocks.
// Convert ATX headings found inline within HTML tags to real HTML heading elements.
// e.g. `<div align="center">## Title</div>` → `<div align="center"><h2>Title</h2></div>`
function convertMarkdownHeadingsInHtml(content: string): string {
  return content.replace(
    /(<[a-zA-Z][^>]*>)(#{1,6}) ([^<\n]+)(<\/[a-zA-Z]+>)/g,
    (_, openTag, hashes, text, closeTag) => {
      const level = hashes.length
      return `${openTag}<h${level}>${text.trim()}</h${level}>${closeTag}`
    }
  )
}

// CommonMark does not process markdown inline syntax (images, links) inside raw HTML blocks.
// Convert markdown images and links found inside HTML block content to their HTML equivalents
// so that rehype-raw can pass them through and component overrides can still be applied.
// e.g. `![alt](url 'title')` → `<img src="url" alt="alt" title="title" />`
// e.g. `[text](url)` → `<a href="url">text</a>`
function convertMarkdownInlineInHtml(content: string): string {
  const blockTags =
    "div|section|article|aside|header|footer|main|nav|figure|figcaption|details|summary"
  return content.replace(
    new RegExp(
      `(<(?:${blockTags})[^>]*>[\\s\\S]*?)(<\\/(?:${blockTags})>)`,
      "g"
    ),
    (_, inner, closeTag) => {
      const processed = inner
        // Images: ![alt](url 'title') or ![alt](url)
        .replace(
          /!\[([^\]]*)\]\(([^)"'\s]+)(?:\s+'([^']*)')?\)/g,
          (_: string, alt: string, src: string, title?: string) =>
            title
              ? `<img src="${src}" alt="${alt}" title="${title}" />`
              : `<img src="${src}" alt="${alt}" />`
        )
        // Links: [text](url)
        .replace(
          /\[([^\]]+)\]\(([^)]+)\)/g,
          (_: string, text: string, href: string) =>
            `<a href="${href}">${text}</a>`
        )
      return `${processed}${closeTag}`
    }
  )
}

// `<link>` is a void element in HTML5 and cannot have children.
// Rename the custom <Link> component tag to <mdlink> before rehype-raw parses it.
function renameLinkTag(content: string): string {
  return content
    .replace(/<Link(\s|>|\/)/g, "<mdlink$1")
    .replace(/<\/Link>/g, "</mdlink>")
}

// CommonMark requires each list item to start on its own line.
// Some CMS entries store inline list items separated by "  * " (two spaces + marker).
// Convert them to separate lines so the list parses correctly.
function normalizeInlineListItems(content: string): string {
  return content.replace(/([^\n])  (\* )/g, "$1\n$2")
}

// A <br> tag on its own line starts a CommonMark type-7 HTML block that consumes
// all following content until a blank line — including list items, which then
// render as literal text instead of a list.
// Ensure a blank line separates <br> from any following list marker.
function normalizeListAfterBr(content: string): string {
  return content.replace(
    /(<br\s*\/?>\s*)\n([ \t]*(?:\d+[.)>]|[-*+])[ \t])/gm,
    "$1\n\n$2"
  )
}

const safeProtocols = /^(https?|ircs?|mailto|xmpp|tel)$/i

function urlTransform(url: string): string {
  const colon = url.indexOf(":")
  const numberSign = url.indexOf("#")
  if (
    colon < 0 ||
    (numberSign !== -1 && colon > numberSign) ||
    safeProtocols.test(url.slice(0, colon))
  ) {
    return url
  }
  return ""
}

/**
 * @info use markdown components from `/components/Markdown` if possible.
 */
function BaseMarkdown({
  components,
  children,
}: BaseMarkdownProps): ReactElement<BaseMarkdownProps> {
  return (
    <ReactMarkdown
      rehypePlugins={[rehypeRaw]}
      components={components}
      urlTransform={urlTransform}
    >
      {renameLinkTag(
        convertMarkdownInlineInHtml(
          convertMarkdownHeadingsInHtml(
            collapseLooseLists(
              normalizeListAfterBr(
                normalizeListMarkerSpacing(normalizeInlineListItems(children))
              )
            )
          )
        )
      )}
    </ReactMarkdown>
  )
}

export default BaseMarkdown
